import {
  Autocomplete,
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { updateUser } from '../../service/user.service';
import { FormProvider, RHFTextField, RHFUploadAvatar } from '../../fl-components/hook-form';
import Label from '../../fl-components/Label';
import { fData } from '../../utils/formatNumber';
import { ConvertToBase64 } from '../../helper/commonHelper';
import { uploadFile } from '../../service/upload.file.service';
import { verifyMobileOtp, sendOtp } from '../../service/auth.service';
import { CHANGE_SELECTED_ORG, UPDATE_USER } from '../../redux/constants';
import { updateOrganization } from '../../service/org.service';
import CollegeSelectionBox from './CollegeSelectionBox';

export default function ProfileDialogContent({
  displayButton,
  handleClose,
  setFormSubmitting,
  setOtpVerifyScreen,
  isOtpScreen,
  formSubmitting
}) {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const selectedOrg = useSelector((state) => state.org.selectedOrg);
  const cityData = useSelector((state) => state.geo.cities);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOTPError] = useState(false);
  const [selectedPicture, setPicture] = useState(null);
  const [showCollegeSelection, setShowCollegeSelection] = useState(false);

  const NewUserSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().required('Email is required').email(),
    phoneNumber: Yup.string(),
    // avatarUrl: Yup.mixed().test('required', 'Avatar is required', (value) => value !== ''),
    dob: Yup.string().required('DOB is required'),
    city: Yup.string().required('City is required'),
    ...(currentUser.profile === 'teacher' && {
      orgName: Yup.string().required('Name is required'),
      address: Yup.string().required('Address is required'),
      website: Yup.string().required('Website is required').url()
    }),
    ...(currentUser.profile === 'student' && {
      selectedCollege: Yup.object().nullable().required('Please select college/school')
    })
  });

  const defaultValues = useMemo(
    () => ({
      name: currentUser?.fullName || '',
      email: currentUser?.email || '',
      phoneNumber: `${currentUser?.mobileNumber}`.split('91')[1] || '',
      avatarUrl: currentUser?.profileImage || '',
      city: currentUser?.city || '',
      dob: currentUser?.dob || '',
      gender: currentUser?.gender || '',
      ...(currentUser.profile === 'teacher' && {
        orgName: selectedOrg?.name || '',
        website: selectedOrg?.website || '',
        address: selectedOrg?.address || ''
      }),
      ...(currentUser.profile === 'student' && {
        selectedCollege: {
          name: currentUser?.schoolName || '',
          id: currentUser?.schoolId || '',
          type: currentUser?.educationType || '',
          view: currentUser?.schoolId ? '' : 'custom'
        }
      })
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser]
  );
  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues
  });

  const { setValue, handleSubmit, getValues, control, setError } = methods;

  const uploadProfilePicture = async () => {
    let profileUrl = '';
    if (selectedPicture) {
      const base64 = await ConvertToBase64(selectedPicture);
      const fileObj = {
        base64,
        fileType: selectedPicture.type,
        fileName: selectedPicture.name,
        uploadFor: 'profileImage',
        userId: currentUser.id
      };

      const res = await uploadFile('', fileObj);
      if (res) {
        profileUrl = res.fileURL;
      }
    } else {
      profileUrl = getValues('avatarUrl');
    }

    return profileUrl;
  };

  const getUserObj = async () => {
    const profileImage = await uploadProfilePicture();
    const orgObj = {};
    const userObj = {
      fullName: getValues('name').trim(),
      email: getValues('email')
      // profileImage: await uploadProfilePicture()
    };
    if (profileImage) userObj.profileImage = profileImage;
    if (getValues('dob')) userObj.dob = moment(getValues('dob')).format('DD/MM/yyyy');
    if (getValues('city')) userObj.city = getValues('city');
    if (getValues('gender')) userObj.gender = getValues('gender');
    if (getValues('phoneNumber')) userObj.mobileNumber = `91${getValues('phoneNumber')}`;
    if (getValues('selectedCollege.name'))
      userObj.schoolName = `${
        getValues('selectedCollege.view') === 'custom'
          ? `Other, ${getValues('selectedCollege.name').trim()}`
          : getValues('selectedCollege.name').trim()
      }`;
    if (getValues('selectedCollege.type'))
      userObj.educationType = getValues('selectedCollege.type');
    if (getValues('selectedCollege.id')) userObj.schoolId = getValues('selectedCollege.id');

    if (currentUser.profile === 'teacher') {
      orgObj.name = getValues('orgName').trim();
      orgObj.address = getValues('address');
      orgObj.website = getValues('website');
      orgObj.email = getValues('email');
      if (getValues('city')) orgObj.city = getValues('city');
      if (profileImage) orgObj.instituteProfileImage = profileImage;
    }
    return { userObj, orgObj };
  };

  // If user profile then org details needs to be update.
  const updateOrgDetails = async () => {
    // If user profile is teacher then update the org details
    if (currentUser.profile === 'teacher') {
      // Update the data for org
      const orgRes = await updateOrganization(
        selectedOrg?.id ?? selectedOrg?.orgId,
        await (
          await getUserObj()
        ).orgObj
      );
      if (orgRes) {
        dispatch({ type: CHANGE_SELECTED_ORG, payload: orgRes });
      }
    }
  };

  const checkIfNumberChanged = async (userObj) => {
    const oldNumber = currentUser?.mobileNumber;
    if (getValues('phoneNumber') !== '' && `${oldNumber}` !== `91${getValues('phoneNumber')}`) {
      handleSendOtp();
    } else {
      const res = await updateUser(currentUser.id, userObj);
      updateOrgDetails();

      if (res) {
        dispatch({ type: UPDATE_USER, payload: res });
        enqueueSnackbar('Profile updated successfully');
        setFormSubmitting(false);
        handleClose();
        setShowCollegeSelection(false);
      }
      setFormSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpValue.trim() === '') {
      setOTPError(true);
      return;
    }
    setFormSubmitting(true);
    const otpObj = {
      mobileNumber: `91${getValues('phoneNumber')}`,
      verificationCode: otpValue,
      updateProfile: true
    };
    const res = await verifyMobileOtp(otpObj);
    if (res) {
      const userRes = await updateUser(currentUser.id, await (await getUserObj()).userObj);
      updateOrgDetails();
      if (userRes) {
        enqueueSnackbar('Profile Updated Successfully');
        setFormSubmitting(false);
        dispatch({ type: UPDATE_USER, payload: userRes });
        setShowCollegeSelection(false);
        handleClose();
      } else {
        enqueueSnackbar('Something went wrong please try again', {
          variant: 'error'
        });
        setFormSubmitting(false);
      }
    } else {
      setFormSubmitting(false);
    }
  };

  const handleSendOtp = async () => {
    // const counCode = phoneCountryCode.replace('+', '');
    const res = await sendOtp(`91${getValues('phoneNumber')}`);
    if (res) {
      enqueueSnackbar('OTP sent to your register number');
      setOtpVerifyScreen(true);
      setFormSubmitting(false);
    } else {
      setFormSubmitting(false);
    }
  };

  const onSubmit = async () => {
    try {
      const collegeData = getValues('selectedCollege');
      if (collegeData?.type && collegeData?.name.toLowerCase() === '') {
        setError('selectedCollege', {
          type: 'required'
        });
        return;
      }
      setFormSubmitting(true);
      checkIfNumberChanged(await (await getUserObj()).userObj);
    } catch (error) {
      console.error(error);
      setFormSubmitting(false);
    }
  };

  const handleDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];

      if (file) {
        setPicture(file);
        setValue(
          'avatarUrl',
          Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        );
      }
    },
    [setValue]
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ py: 10, px: 3 }}>
            <Label
              color="success"
              sx={{ textTransform: 'uppercase', position: 'absolute', top: 24, right: 24 }}
            >
              active
            </Label>

            <Box sx={{ mb: 5 }}>
              <RHFUploadAvatar
                name="avatarUrl"
                accept="image/*"
                maxSize={3145728}
                onDrop={handleDrop}
                helperText={
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 2,
                      mx: 'auto',
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.secondary'
                    }}
                  >
                    Allowed *.jpeg, *.jpg, *.png
                    <br /> max size of {fData(3145728)}
                  </Typography>
                }
              />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              // sx={{
              //   display: 'flex',
              //   columnGap: 2,
              //   rowGap: 3,
              //   flexDirection: 'column'
              //   // gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' }
              // }}
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' }
              }}
            >
              <RHFTextField name="name" label="Full Name" />
              <RHFTextField name="email" label="Email Address" disabled={currentUser.isGmailLink} />
              <RHFTextField
                name="phoneNumber"
                label="Phone Number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">+91</InputAdornment>
                }}
              />
              <FormControl>
                <FormLabel>Gender</FormLabel>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      row
                      value={getValues('gender')}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <FormControlLabel
                        value="male"
                        control={<Radio size="small" sx={{ py: '6px' }} />}
                        label="Male"
                      />
                      <FormControlLabel
                        value="female"
                        control={<Radio size="small" sx={{ py: '6px' }} />}
                        label="Female"
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
              <Controller
                name="dob"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      label="DOB"
                      maxDate={new Date()}
                      inputFormat="dd/MM/yyyy"
                      value={getValues('dob')}
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} error={!!error} helperText={error?.message} />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
              <Controller
                name="city"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    // {...field}
                    options={cityData}
                    value={getValues('city')}
                    onChange={(event, newValue) => {
                      field.onChange(newValue?.name || '');
                    }}
                    getOptionLabel={(option) => option.name || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />

              {currentUser.profile === 'student' &&
                (getValues('selectedCollege.name') && !showCollegeSelection ? (
                  <TextField
                    value={getValues('selectedCollege.name')}
                    label="School/College"
                    onClick={() => {
                      setShowCollegeSelection(true);
                    }}
                    disabled
                  />
                ) : (
                  (getValues('selectedCollege.name') === '' || showCollegeSelection) && (
                    <Controller
                      name="selectedCollege"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <CollegeSelectionBox
                          onChange={(d) => field.onChange(d)}
                          error={error}
                          usedFrom="profile"
                        />
                      )}
                    />
                  )
                ))}
              {isOtpScreen && (
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <RHFTextField
                    name="otpValue"
                    label="Enter OTP"
                    onChange={(e) => setOtpValue(e.target.value)}
                    error={otpError}
                    helperText={otpError ? 'Please enter OTP' : ''}
                  />
                  <Button onClick={() => handleSendOtp()}>Resent OTP</Button>
                </Stack>
              )}
              {currentUser.profile === 'teacher' && (
                <>
                  <RHFTextField name="orgName" label="Class Name" />
                  <RHFTextField name="address" label="Address" />
                  <RHFTextField name="website" label="Website" />
                </>
              )}
            </Box>

            <Stack
              alignItems="flex-end"
              sx={{ mt: 3 }}
              justifyContent="end"
              flex={1}
              display={displayButton ? '' : 'none'}
            >
              {isOtpScreen ? (
                <LoadingButton
                  variant="contained"
                  id="verifyOtpButton"
                  loading={formSubmitting}
                  onClick={() => handleVerifyOtp()}
                >
                  Verify
                </LoadingButton>
              ) : (
                <LoadingButton
                  type="submit"
                  id="saveChangeButton"
                  variant="contained"
                  loading={formSubmitting}
                >
                  {!currentUser ? 'Create User' : 'Save Changes'}
                </LoadingButton>
              )}
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}

ProfileDialogContent.defaultProps = {
  handleClose: () => {},
  isOtpScreen: false,
  displayButton: false,
  formSubmitting: false
};

ProfileDialogContent.propTypes = {
  handleClose: PropTypes.func,
  setFormSubmitting: PropTypes.func,
  setOtpVerifyScreen: PropTypes.func,
  isOtpScreen: PropTypes.bool,
  displayButton: PropTypes.bool,
  formSubmitting: PropTypes.bool
};
